<script setup lang="ts">
  import { NMenu, NScrollbar } from 'naive-ui'

  import { isHolidaySeason, isValentineSeason, useScreenSize } from '@/shared/lib'
  import { SignoutPopup } from '@/shared/ui/components'

  import { UserBaseInfo } from '@/entities/profile'

  import { SearchInput } from '@/features/search'
  import { TenantSwitcher } from '@/features/tenantSwitcher'

  // eslint-disable-next-line fsd/no-cross-slice-imports
  import { DynamicSearch } from '@/widgets/dynamicSearch'
  import { TheHearts, TheSnowflakes, useSidebarMenu, useSidebarSearch } from '@/widgets/layout'
  import { useNavigation } from '@/widgets/layout/model'

  const emit = defineEmits<{
    (event: 'close-sidebar'): void
  }>()

  const { isMobile, isTablet } = storeToRefs(useScreenSize())

  const onCloseSidebar = () => emit('close-sidebar')

  const { navigation } = useNavigation(isMobile)
  const { menuOptions, activeKey, handleMenuSelect, isSignoutPopupOpen } = useSidebarMenu({
    navigation,
    isMobile,
    onCloseSidebar,
  })
  const { searchInput, searchInputInFocus, handleFocus, handleBlurDebounced, searchHandler } = useSidebarSearch({
    isMobile,
    onCloseSidebar,
  })

  const showSnowflakes = computed(() => isTablet.value && isHolidaySeason())
  const showHearts = computed(() => isTablet.value && isValentineSeason())

  const isTenantDebugEnabled = import.meta.env.VITE_TENANT_DEBUG_ENABLED === 'true'
</script>

<template>
  <div class="sidebar-wrapper">
    <SignoutPopup v-model="isSignoutPopupOpen" />

    <NScrollbar
      class="shadow-inner-huge h-screen-content max-h-screen-content relative z-4 w-70 max-w-70 min-w-0 shrink-0 grow-0 basis-70 px-4 py-6 [&_.n-scrollbar-container]:pr-1"
    >
      <TheSnowflakes v-if="showSnowflakes" />
      <TheHearts v-if="showHearts" />

      <div class="flex h-full w-full flex-col items-start gap-4">
        <!-- TEMPORARY: Dev tenant switcher. TODO: Remove after testing -->
        <TenantSwitcher
          v-if="isTenantDebugEnabled"
          class="w-full"
        />

        <UserBaseInfo v-if="isMobile" />

        <div class="relative w-[calc(100%-4px)]">
          <SearchInput
            v-model="searchInput"
            @search="searchHandler"
            @focus="handleFocus"
            @blur="handleBlurDebounced"
          />
          <DynamicSearch
            class="top-11"
            :search-query="searchInput"
            search-page
            :search-input-in-focus="searchInputInFocus"
            @select-item="emit('close-sidebar')"
          />

          <hr class="text-border mt-4" />
        </div>

        <NMenu
          class="w-full min-w-0 [&_.n-menu-item]:mt-2 [&_.n-menu-item-content]:px-4! [&_.n-menu-item-content]:py-2.25! [&_.n-menu-item-content-header]:overflow-hidden [&_.n-menu-item-content-header]:font-medium [&_.n-menu-item-content-header]:text-ellipsis [&_.n-menu-item-content-header]:whitespace-nowrap [&_.n-menu-item-content::before]:inset-0 [&_.n-menu-item-content\_\_icon]:mr-1! [&_.n-menu-item-content\_\_icon_svg]:h-4.5 [&_.n-menu-item-content\_\_icon_svg]:w-4 [&_.n-menu-item-group-title]:pl-0!"
          :value="activeKey"
          :options="menuOptions"
          :indent="0"
          :root-indent="0"
          @update:value="handleMenuSelect"
        />
      </div>
    </NScrollbar>
  </div>
</template>
